import {
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";

const GREEN = "#00A63E";
const RED = "#BE3018";

const FONT = '"Helvetica Neue", Arial, sans-serif';

const headerCellSx = {
  border: "none",
  fontFamily: FONT,
  fontSize: "12px",
  fontWeight: 700,
  color: "#454545",
  backgroundColor: "#EEF0F1",
  py: 1,
};

const bodyCellBaseSx = {
  border: "none",
  borderBottom: "1px solid #E0E0E0",
  fontFamily: FONT,
  fontSize: "14px",
  fontWeight: 400,
  color: "#454545",
  backgroundColor: "#FFFFFF",
  py: 1.25,
};

const tierBodyCellSx = {
  ...bodyCellBaseSx,
  verticalAlign: "top",
};

const metricBodyCellSx = {
  ...bodyCellBaseSx,
  verticalAlign: "middle",
};

/** Maps `Reach_Flag` / `Frequency_Flag` from KPI API (`Greater Than` / `Less Than`). */
function trendFromReachFrequencyFlag(flag) {
  if (flag === null || flag === undefined || flag === "") return null;
  const s = String(flag)
    .trim()
    .toUpperCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ");
  if (s === "GREATER THAN") return "above";
  if (s === "LESS THAN") return "below";
  return null;
}

/** Up / down triangle (CSS borders). */
function TrendTriangle({ direction, sizePx = 8 }) {
  const isUp = direction === "up";
  const color = isUp ? GREEN : RED;
  const half = Math.max(2, Math.round(sizePx * 0.48));
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: "inline-block",
        width: 0,
        height: 0,
        flexShrink: 0,
        ml: 0.5,
        lineHeight: 0,
        verticalAlign: "middle",
        borderLeft: `${half}px solid transparent`,
        borderRight: `${half}px solid transparent`,
        ...(isUp
          ? {
              borderBottom: `${sizePx}px solid ${color}`,
              borderTop: 0,
            }
          : {
              borderTop: `${sizePx}px solid ${color}`,
              borderBottom: 0,
            }),
      }}
    />
  );
}

function MetricValue({ children, flag }) {
  const trend = trendFromReachFrequencyFlag(flag);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "nowrap",
        gap: 0,
        minWidth: 0,
        width: "100%",
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: "#454545",
          lineHeight: 1.2,
          flexShrink: 0,
        }}
      >
        {children}
      </Typography>
      {trend ? (
        <TrendTriangle
          direction={trend === "above" ? "up" : "down"}
          sizePx={9}
        />
      ) : null}
    </Box>
  );
}

const tableContainerSx = {
  maxHeight: "282px",
  overflowY: "auto",
  overflowX: "hidden",
  pr: 0.5,
  width: "100%",
  maxWidth: "100%",
};

function TableSkeleton() {
  return (
    <TableContainer sx={{ ...tableContainerSx, mt: 2 }}>
      <Stack spacing={1.2} sx={{ py: 1, pb: MY_PERF_SCROLL_LIST_PB }}>
        {[...Array(5)].map((_, idx) => (
          <Skeleton key={idx} variant="rounded" height={24} />
        ))}
      </Stack>
    </TableContainer>
  );
}

export default function ReachAndFrequencyCard({
  title = "Reach and Frequency",
  subtitle = "Average reach and frequency of engagement by tier",
  rows = [],
  isLoading = false,
  onExplore,
}) {
  const hasRows = Array.isArray(rows) && rows.length > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: "12px",
        border: "1px solid #E5E7EB",
        boxShadow: "1px 4px 4px 0px #00000008",
      }}
    >
      <CardContent
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: 3 },
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                width: "100%",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: 1.2,
                  color: "#000",
                }}
              >
                {title}
              </Typography>
              {onExplore && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => onExplore()}
                  sx={{
                    textTransform: "none",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#D96B04",
                    minWidth: "auto",
                    p: 0,
                    flexShrink: 0,
                    marginRight: "30px",
                  }}
                >
                  Explore
                </Button>
              )}
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                color: "#718096",
                mt: 0.75,
                lineHeight: 1.3,
              }}
            >
              {subtitle}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{ mt: 1.25 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TrendTriangle direction="up" sizePx={10} />
                <Typography
                  sx={{ fontSize: 12, color: "#718096", lineHeight: 1.2 }}
                >
                  Above National Average
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TrendTriangle direction="down" sizePx={10} />
                <Typography
                  sx={{ fontSize: 12, color: "#718096", lineHeight: 1.2 }}
                >
                  Below National Average
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Stack>

        {isLoading ? (
          <TableSkeleton />
        ) : (
          <TableContainer sx={{ ...tableContainerSx, mt: 2 }}>
            {!hasRows ? (
              <Box
                sx={{
                  minHeight: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#FFFFFF",
                }}
              >
                <Typography
                  fontSize={10}
                  sx={{ color: "#000", textAlign: "center", fontFamily: FONT }}
                >
                  No data found
                </Typography>
              </Box>
            ) : (
              <Box sx={{ pb: MY_PERF_SCROLL_LIST_PB }}>
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "100%",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          ...headerCellSx,
                          textAlign: "left",
                          width: "33.34%",
                        }}
                      >
                        Tier
                      </TableCell>
                      <TableCell
                        sx={{
                          ...headerCellSx,
                          textAlign: "right",
                          width: "33.33%",
                        }}
                      >
                        Avg. Reach (QTD)
                      </TableCell>
                      <TableCell
                        sx={{
                          ...headerCellSx,
                          textAlign: "right",
                          width: "33.33%",
                        }}
                      >
                        Avg. Frequency (YTD)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={row.id ?? `${row.tierLabel}-${idx}`}>
                        <TableCell
                          sx={{
                            ...tierBodyCellSx,
                            textAlign: "left",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#2D2D2D",
                              lineHeight: 1.2,
                            }}
                          >
                            {row.tierLabel ?? "-"}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: "#718096",
                              mt: "2px",
                              lineHeight: 1.2,
                            }}
                          >
                            {row.tierSubtitle ?? ""}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...metricBodyCellSx,
                            textAlign: "right",
                            fontWeight: 700,
                          }}
                        >
                          <MetricValue flag={row.reachFlag}>
                            {row.avgReachPercent != null
                              ? `${Math.round(row.avgReachPercent)}%`
                              : "—"}
                          </MetricValue>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...metricBodyCellSx,
                            textAlign: "right",
                            fontWeight: 700,
                          }}
                        >
                          <MetricValue flag={row.frequencyFlag}>
                            {row.avgFrequency != null
                              ? Number(row.avgFrequency).toFixed(1)
                              : "—"}
                          </MetricValue>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
